import React, { useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Lightweight image lightbox. Click anywhere outside the image or press
 * Escape to close. Uses a fixed full-screen overlay.
 *
 * Props:
 *  - src: image source (data URL or absolute URL)
 *  - alt: optional alt text
 *  - caption: optional caption rendered below the image
 *  - onClose: () => void  REQUIRED
 */
export function ImageLightbox({ src, alt = "", caption, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    // lock scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!src) return null;

  return (
    <div
      className="fixed inset-0 z-[300] flex flex-col items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="image-lightbox"
      onClick={onClose}
    >
      <Button
        size="icon"
        variant="secondary"
        className="absolute right-4 top-4 z-10 h-9 w-9 rounded-full bg-white/95 text-slate-800 shadow-md hover:bg-white"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        data-testid="image-lightbox-close"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </Button>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
        data-testid="image-lightbox-img"
      />
      {caption ? (
        <div
          className="mt-3 max-w-[92vw] text-center text-xs text-white/85"
          onClick={(e) => e.stopPropagation()}
        >
          {caption}
        </div>
      ) : null}
    </div>
  );
}

export default ImageLightbox;
